import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { useTranslation } from "react-i18next";

const NAV_ITEMS = [
    { labelKey: "nav.links.Features", defaultLabel: "Features", href: "#manifesto" },
    { labelKey: "nav.links.TravelDate", defaultLabel: "TravelDate", href: "#traveldate" },
    { labelKey: "nav.links.Community", defaultLabel: "Community", href: "#community" },
    { labelKey: "nav.links.Pricing", defaultLabel: "Pricing", href: "#pricing" },
    { labelKey: "nav.links.Business", defaultLabel: "Business", href: "#business" },
];

export default function Nav() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { user, signout } = useAuth() || {};
    const nav = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
                className="fixed top-0 left-0 right-0 z-50"
                data-testid="site-nav"
            >
                <div
                    className={`transition-all duration-500 ${scrolled ? "py-3" : "py-5"}`}
                    style={{
                        background: scrolled ? "rgba(11,19,32,0.85)" : "rgba(11,19,32,0.55)",
                        backdropFilter: "blur(20px) saturate(140%)",
                        WebkitBackdropFilter: "blur(20px) saturate(140%)",
                        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(255,255,255,0.03)",
                    }}
                >
                    <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
                            <PassportLogo size={38} />
                            <div className="hidden sm:flex flex-col leading-none">
                                <span className="font-serif text-xl text-white tracking-tight">Passport</span>
                                <span className="overline" style={{ fontSize: "9px", letterSpacing: "0.25em" }}>The Social Travel Network</span>
                            </div>
                        </Link>

                        <nav className="hidden lg:flex items-center gap-10">
                            {NAV_ITEMS.map((l) => (
                                <a
                                    key={l.defaultLabel}
                                    href={l.href}
                                    className="text-sm text-white/80 hover:text-[color:var(--gold)] transition-colors duration-300 relative group"
                                    data-testid={`nav-link-${l.defaultLabel.toLowerCase()}`}
                                >
                                    {t(l.labelKey)}
                                    <span className="absolute -bottom-1 left-0 right-0 h-px scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" style={{ background: "var(--gold)" }} />
                                </a>
                            ))}
                        </nav>

                        <div className="flex items-center gap-3">
                            {user ? (
                                <>
                                    <button
                                        onClick={() => nav("/app")}
                                        className="hidden sm:inline-flex btn-ghost text-sm !py-2.5 !px-5"
                                        data-testid="nav-open-app"
                                    >
                                        {t("nav.openApp")}
                                    </button>
                                    <button
                                        onClick={signout}
                                        className="hidden md:inline-flex btn-gold text-sm !py-2.5 !px-5"
                                        data-testid="nav-signout"
                                    >
                                        {t("nav.signOut")}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link to="/auth" className="hidden sm:inline-flex btn-ghost text-sm !py-2.5 !px-5" data-testid="nav-signin">
                                        {t("nav.signIn")}
                                    </Link>
                                    <Link to="/auth?mode=signup" className="btn-gold text-sm !py-2.5 !px-5" data-testid="nav-get-started">
                                        {t("nav.getStarted")}
                                    </Link>
                                </>
                            )}
                            <button
                                onClick={() => setOpen(true)}
                                className="lg:hidden ml-2 w-10 h-10 rounded-full glass grid place-items-center"
                                data-testid="nav-menu-open"
                                aria-label={t("nav.openMenuAria")}
                            >
                                <List size={18} weight="regular" />
                            </button>
                        </div>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] grain"
                        style={{ background: "rgba(5,10,17,0.98)" }}
                        data-testid="mobile-menu"
                    >
                        <div className="flex items-center justify-between px-6 py-5">
                            <PassportLogo size={38} />
                            <button
                                onClick={() => setOpen(false)}
                                className="w-10 h-10 rounded-full glass grid place-items-center"
                                data-testid="nav-menu-close"
                                aria-label={t("nav.closeMenuAria")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="px-6 pt-10 flex flex-col gap-6">
                            {NAV_ITEMS.map((l, i) => (
                                <motion.a
                                    key={l.defaultLabel}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.05 * i, duration: 0.5 }}
                                    className="font-serif text-4xl text-white"
                                >
                                    <span className="overline mr-3">0{i + 1}</span>
                                    {t(l.labelKey)}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
